import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LogOut, User, Mail, Shield, Key, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);

    const doLogout = () => {
        logout();
        navigate('/login');
    };

    const changePassword = async (e) => {
        e.preventDefault();
        setMsg('');
        setErr('');
        setLoading(true);
        try {
            await api.post('/auth/change-password', { current_password: current, new_password: next });
            setMsg('Password updated');
            setCurrent('');
            setNext('');
        } catch (e2) {
            setErr(e2.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="profile-page">
            <header>
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#111827]">Profile</h1>
                <p className="text-sm text-[#6B7280]">Manage your account settings.</p>
            </header>

            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="brand-card p-6"
            >
                <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3A94] to-[#3552B8] text-xl font-extrabold text-white">
                        {(user?.full_name || user?.username || '?')[0].toUpperCase()}
                    </div>
                    <div>
                        <div className="text-xl font-bold text-[#111827]">
                            {user?.full_name || user?.username}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-[#6B7280]">
                            <Shield className="h-3.5 w-3.5" />
                            <span className="uppercase tracking-wide font-semibold">{user?.role}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-6 grid gap-3 md:grid-cols-2">
                    <InfoRow icon={User} label="Username" value={user?.username} />
                    <InfoRow icon={Mail} label="Email" value={user?.email} />
                    <InfoRow
                        icon={CheckCircle2}
                        label="Status"
                        value={user?.is_active ? 'Active' : 'Inactive'}
                    />
                    <InfoRow
                        icon={Key}
                        label="Joined"
                        value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                    />
                </div>
            </motion.section>

            <section className="brand-card p-6">
                <h3 className="text-sm font-bold text-[#111827]">Change Password</h3>
                <form onSubmit={changePassword} className="mt-4 grid gap-3 md:grid-cols-2 items-end">
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-[#111827]">Current password</label>
                        <input
                            data-testid="current-password"
                            type="password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            className="brand-input"
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-[#111827]">New password</label>
                        <input
                            data-testid="new-password"
                            type="password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            className="brand-input"
                            required
                            minLength={6}
                        />
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between gap-3">
                        <div className="text-xs">
                            {msg && <span className="text-emerald-600 font-medium">{msg}</span>}
                            {err && <span className="text-red-600 font-medium">{err}</span>}
                        </div>
                        <button
                            data-testid="change-password-btn"
                            type="submit"
                            className="brand-btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Updating…' : 'Update password'}
                        </button>
                    </div>
                </form>
            </section>

            <div className="flex justify-end">
                <button
                    data-testid="profile-logout-btn"
                    onClick={doLogout}
                    className="brand-btn-outline gap-2"
                >
                    <LogOut className="h-4 w-4" /> Sign out
                </button>
            </div>
        </div>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#1C3A94]">
                <Icon className="h-4 w-4" />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">{label}</div>
                <div className="text-sm font-semibold text-[#111827]">{value || '—'}</div>
            </div>
        </div>
    );
}
